import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  MapPin, 
  Clock, 
  Activity,
  TrendingUp,
  TrendingDown,
  Settings,
  Search,
  Filter,
  ChevronDown,
  Bell,
  Calendar,
  Shield,
  Users
} from 'lucide-react';
import {
  FallIncident,
  RiskZone,
  TimeBasedRisk,
  PreventiveMeasure,
  FallDetectionSettings,
  FallPreventionProgress
} from './types';
import {
  generateFallIncidents,
  generateRiskZones,
  generateTimeBasedRisks,
  generatePreventiveMeasures,
  getDefaultFallDetectionSettings,
  generateFallPreventionProgress
} from './utils/FallDataSimulator';
import DashboardTab from './tabs/DashboardTab';
import HistoryTab from './tabs/HistoryTab';
import RiskAssessmentTab from './tabs/RiskAssessmentTab';
import PreventionTab from './tabs/PreventionTab';
import SettingsTab from './tabs/SettingsTab';

type ActiveTab = 'dashboard' | 'history' | 'risk' | 'prevention' | 'settings';

const FallDetection: React.FC = () => {
  // State for fall data
  const [incidents, setIncidents] = useState<FallIncident[]>([]);
  const [riskZones, setRiskZones] = useState<RiskZone[]>([]);
  const [timeBasedRisks, setTimeBasedRisks] = useState<TimeBasedRisk[]>([]);
  const [preventiveMeasures, setPreventiveMeasures] = useState<PreventiveMeasure[]>([]);
  const [settings, setSettings] = useState<FallDetectionSettings>(getDefaultFallDetectionSettings());
  const [progress, setProgress] = useState<FallPreventionProgress | null>(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterBy, setFilterBy] = useState<{
    severity: ('HIGH' | 'MEDIUM' | 'LOW')[];
    location: string[];
    status: ('RESOLVED' | 'UNDER_REVIEW' | 'PENDING')[];
  }>({
    severity: [],
    location: [],
    status: []
  });

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIncidents(generateFallIncidents(10));
      setRiskZones(generateRiskZones());
      setTimeBasedRisks(generateTimeBasedRisks());
      setPreventiveMeasures(generatePreventiveMeasures());
      setProgress(generateFallPreventionProgress());
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Filter incidents based on search and filters
  const filteredIncidents = incidents.filter(incident => {
    // Search filter
    if (searchTerm && !incident.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !incident.location.room.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Severity filter
    if (filterBy.severity.length > 0 && !filterBy.severity.includes(incident.severity)) {
      return false;
    }
    
    // Location filter
    if (filterBy.location.length > 0 && !filterBy.location.includes(incident.location.room)) {
      return false;
    }
    
    // Status filter
    if (filterBy.status.length > 0 && !filterBy.status.includes(incident.status)) {
      return false;
    }
    
    return true;
  });

  // Toggle filters
  const toggleSeverityFilter = (severity: 'HIGH' | 'MEDIUM' | 'LOW') => {
    setFilterBy(prev => {
      const severities = [...prev.severity];
      if (severities.includes(severity)) {
        return { ...prev, severity: severities.filter(s => s !== severity) };
      } else {
        return { ...prev, severity: [...severities, severity] };
      }
    });
  };

  const toggleLocationFilter = (location: string) => {
    setFilterBy(prev => {
      const locations = [...prev.location];
      if (locations.includes(location)) {
        return { ...prev, location: locations.filter(l => l !== location) };
      } else {
        return { ...prev, location: [...locations, location] };
      }
    });
  };

  const toggleStatusFilter = (status: 'RESOLVED' | 'UNDER_REVIEW' | 'PENDING') => {
    setFilterBy(prev => {
      const statuses = [...prev.status];
      if (statuses.includes(status)) {
        return { ...prev, status: statuses.filter(s => s !== status) };
      } else {
        return { ...prev, status: [...statuses, status] };
      }
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterBy({
      severity: [],
      location: [],
      status: []
    });
    setSearchTerm('');
  };

  // Get summary statistics
  const summaryData = {
    total: incidents.length,
    high: incidents.filter(i => i.severity === 'HIGH').length,
    resolved: incidents.filter(i => i.status === 'RESOLVED').length,
    avgResponse: Math.round(
      incidents.reduce((acc, inc) => acc + inc.responseTime, 0) / incidents.length
    ),
    highRiskZones: riskZones.filter(z => z.riskLevel === 'HIGH').length,
    implementedMeasures: preventiveMeasures.filter(m => m.status === 'IMPLEMENTED').length
  };

  // Get color based on severity
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  // Get background color based on risk level
  const getRiskColor = (risk: number) => {
    if (risk >= 70) return 'text-red-500';
    if (risk >= 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Loading fall detection data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Fall Detection & Prevention</h2>
        <p className="text-gray-600">
          Comprehensive monitoring and analysis of fall incidents with AI-powered risk assessment and prevention strategies.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total Falls</h3>
            <AlertTriangle className="text-blue-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.total}</p>
          <p className="text-xs text-gray-500">Last 90 days</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">High Severity</h3>
            <TrendingUp className="text-red-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.high}</p>
          <p className="text-xs text-gray-500">Require immediate action</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Resolved</h3>
            <Shield className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.resolved}</p>
          <p className="text-xs text-gray-500">Cases addressed</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Avg Response</h3>
            <Clock className="text-purple-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.avgResponse}s</p>
          <p className="text-xs text-gray-500">Response time</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">High Risk Areas</h3>
            <MapPin className="text-orange-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.highRiskZones}</p>
          <p className="text-xs text-gray-500">Zones identified</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Safety Measures</h3>
            <Settings className="text-indigo-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.implementedMeasures}</p>
          <p className="text-xs text-gray-500">Implemented</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'dashboard'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Activity className="inline-block mr-2 w-5 h-5" />
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'history'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Calendar className="inline-block mr-2 w-5 h-5" />
          Fall History
        </button>
        <button
          onClick={() => setActiveTab('risk')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'risk'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <AlertTriangle className="inline-block mr-2 w-5 h-5" />
          Risk Assessment
        </button>
        <button
          onClick={() => setActiveTab('prevention')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'prevention'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Shield className="inline-block mr-2 w-5 h-5" />
          Prevention
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'settings'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Bell className="inline-block mr-2 w-5 h-5" />
          Alert Settings
        </button>
      </div>

      {/* Content based on active tab */}
      <div>
        {activeTab === 'dashboard' && (
          <DashboardTab 
            incidents={incidents}
            riskZones={riskZones}
            timeBasedRisks={timeBasedRisks}
            progress={progress}
          />
        )}
        
        {activeTab === 'history' && (
          <HistoryTab 
            incidents={filteredIncidents}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filterBy={filterBy}
            toggleSeverityFilter={toggleSeverityFilter}
            toggleLocationFilter={toggleLocationFilter}
            toggleStatusFilter={toggleStatusFilter}
            clearFilters={clearFilters}
          />
        )}
        
        {activeTab === 'risk' && (
          <RiskAssessmentTab 
            riskZones={riskZones}
            timeBasedRisks={timeBasedRisks}
            incidents={incidents}
          />
        )}
        
        {activeTab === 'prevention' && (
          <PreventionTab 
            preventiveMeasures={preventiveMeasures}
            progress={progress}
          />
        )}
        
        {activeTab === 'settings' && (
          <SettingsTab 
            settings={settings}
            onSaveSettings={setSettings}
          />
        )}
      </div>
    </div>
  );
};

export default FallDetection; 